import React from 'react';
import {
  Alert,
  AlertDescription,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { PayerResponseSummary } from '../../../services/api/types.gen';
import {
  formatDateForDisplay,
  formatDateTimeInPT,
} from '../../../utils/dateUtils';
import { PayerReasonList } from './PayerReasonList';
import { usePayerResponse } from './usePayerResponse';

/**
 * Why the bill came back, and what the biller may do about it (EMR-1241).
 *
 * One drawer for the Billing Manager and the NOA Manager: both read the shared
 * payer-response read model (EMR-1240), so neither screen carries its own copy
 * of the rule that decides whether a refusal is correctable or must be appealed.
 */
export type PayerReasonKind = 'claim' | 'noa';

/**
 * A claim-level 835 adjustment is routine on a paid claim. A service-line
 * adjustment says the payer priced individual lines differently from the bill,
 * which the biller must look at. It does NOT prove a refusal: a line-level CARC
 * can be a contractual write-off (CO-45), a deductible or coinsurance (PR-1,
 * PR-2). The banner therefore reports the adjustment, not a verdict, and the
 * reason list below it carries the CARC that says which it was.
 */
const hasServiceLineAdjustment = (summary: PayerResponseSummary): boolean =>
  summary.reasons.some(
    reason => reason.source === 'payer_835' && reason.level === 'SERVICE_LINE'
  );

export const PayerReasonDrawer: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: PayerReasonKind;
  id: string;
  status: string;
}> = ({ open, onOpenChange, kind, id, status }) => {
  const { summary, isLoading, errorMessage } = usePayerResponse(kind, id, open);

  const partiallyPaid =
    summary !== null && status === 'PAID' && hasServiceLineAdjustment(summary);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='w-full sm:w-[500px] sm:max-w-none flex flex-col overflow-y-auto'
      >
        <SheetHeader>
          <SheetTitle>Payer response</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading
          </div>
        ) : null}

        {!isLoading && errorMessage ? (
          <Alert className='border-red-200 bg-red-50'>
            <AlertDescription className='text-red-800'>
              {errorMessage}
            </AlertDescription>
          </Alert>
        ) : null}

        {!isLoading && summary ? (
          <div className='flex min-w-0 flex-col gap-4'>
            {summary.payer_claim_control_number ? (
              <p className='text-sm text-muted-foreground'>
                Claim # {summary.payer_claim_control_number}
              </p>
            ) : null}

            {partiallyPaid ? (
              <Alert className='border-amber-200 bg-amber-50'>
                <AlertTriangle className='h-4 w-4 text-amber-600' />
                <AlertDescription className='text-amber-800'>
                  {/* Not "refused part of it": a service-line adjustment can
                      be a contractual write-off, a deductible or coinsurance,
                      none of which is a refusal. Say what the 835 shows. */}
                  The payer adjusted one or more service lines on this claim.
                  Review the service lines.
                </AlertDescription>
              </Alert>
            ) : null}

            <PayerReasonList
              reasons={summary.reasons}
              payerClaimNumber={summary.payer_claim_control_number}
              kind={kind}
            />

            {summary.reasons.length === 0 && summary.last_checked_at ? (
              <p className='text-xs text-muted-foreground'>
                Last checked {formatDateTimeInPT(summary.last_checked_at)}
              </p>
            ) : null}

            {summary.appeal_deadline ? (
              <p className='text-sm text-foreground'>
                Appeal by {formatDateForDisplay(summary.appeal_deadline)}
              </p>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default PayerReasonDrawer;
